import React, { useEffect, useState } from 'react';

/**
 * Customer-facing price-history sparkline. Reads from the public
 * /api/products/:id/price-history endpoint (a non-admin alias of
 * the existing admin endpoint, see seo.js).
 */
export default function PriceHistory({ productId, currentPrice, days = 90 }) {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    if (!productId) return;
    fetch(`/api/products/${productId}/price-history?days=${days}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setPoints(Array.isArray(d) ? d : []))
      .catch(() => setPoints([]));
  }, [productId, days]);

  if (!points || points.length < 2) return null;

  const series = points
    .map(p => Number(p.market_price ?? p.applied_price ?? 0))
    .filter(v => v > 0);
  if (series.length < 2) return null;

  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = Math.max(max - min, 0.01);
  const W = 600, H = 60, P = 4;
  const pts = series.map((v, i) => {
    const x = P + (i / (series.length - 1)) * (W - P * 2);
    const y = H - P - ((v - min) / span) * (H - P * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  const direction = series[series.length - 1] - series[0];
  const trendColor = direction >= 0 ? 'var(--color-success)' : 'var(--color-danger)';

  return (
    <div className="price-history" aria-label="Price history">
      <h4 className="price-history__heading">
        Market price · last {days} days
        <span style={{ marginLeft: 8, color: trendColor, fontWeight: 700 }}>
          {direction >= 0 ? '↑' : '↓'} {Math.abs(direction).toFixed(2)}
        </span>
      </h4>
      <svg className="price-history__chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <polyline
          points={pts}
          fill="none"
          stroke={trendColor}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
      <div className="price-history__caption">
        <span>Low ${min.toFixed(2)}</span>
        <span>High ${max.toFixed(2)}</span>
      </div>
    </div>
  );
}
